import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, Trash2, ExternalLink, RefreshCw, Copy, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { isoUtcToMadridLocal, madridLocalToIsoUtc, previewMadrid } from '../../lib/madridTime';

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))]">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-[hsl(var(--text-tertiary))]">{hint}</p>}
  </div>
);

const STATUS_LABELS = {
  draft:     'Borrador (no se muestra en la web)',
  published: 'Publicada (visible en la web)',
  archived:  'Archivada (oculta, conservada)',
};

export default function AdminMasterclassEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/masterclasses/${slug}`);
      setData(r.data);
      setForm(r.data.masterclass || {});
    } catch {
      toast.error('No se pudo cargar la masterclass');
      navigate('/admin/masterclass');
    }
  };

  useEffect(() => { load(); }, [slug]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Only send mutable fields
      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        description: form.description,
        cover_image_url: form.cover_image_url,
        date_iso: form.date_iso,
        duration_minutes: form.duration_minutes,
        meet_url: form.meet_url,
        recording_url: form.recording_url,
        recording_available_until: form.recording_available_until,
        status: form.status,
        show_homepage_banner: form.show_homepage_banner,
        show_nav_link: form.show_nav_link,
        show_formaciones_card: form.show_formaciones_card,
        show_limites_upsell_card: form.show_limites_upsell_card,
        cta_target_url: form.cta_target_url,
        whatsapp_channel_url: form.whatsapp_channel_url,
      };
      await api.put(`/admin/masterclasses/${slug}`, payload);
      toast.success('Cambios guardados');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const activateRecording = async () => {
    setActivating(true);
    try {
      const r = await api.post(`/admin/masterclasses/${slug}/activate-recording`);
      const until = r.data.masterclass?.recording_available_until;
      toast.success(`Grabación activa hasta ${new Date(until).toLocaleString('es-ES')}`);
      load();
    } catch { toast.error('Error al activar la grabación'); }
    finally { setActivating(false); }
  };

  const clone = async () => {
    try {
      const r = await api.post(`/admin/masterclasses/${slug}/clone`);
      toast.success('Clonada como borrador');
      navigate(`/admin/masterclass/${r.data.slug}`);
    } catch { toast.error('Error al clonar'); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminar "${data.masterclass.title}" y todas sus inscripciones. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/masterclasses/${slug}`);
      toast.success('Eliminada');
      navigate('/admin/masterclass');
    } catch { toast.error('Error al eliminar'); }
  };

  const removeEnrollment = async (id) => {
    if (!window.confirm('¿Eliminar esta inscripción?')) return;
    try {
      await api.delete(`/admin/masterclasses/${slug}/enrollments/${id}`);
      toast.success('Inscripción eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const exportCSV = () => {
    if (!data?.enrollments?.length) return;
    const rows = [['Nombre', 'Email', 'Inscrita el']];
    data.enrollments.forEach(e => rows.push([e.name || '', e.email || '', e.enrolled_at || '']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-inscritas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const recordingUntil = form.recording_available_until || data.masterclass.recording_available_until;
  const phaseLabel = data.masterclass?.phase ? ` · Fase: ${data.masterclass.phase}` : '';

  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA] pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <Link to="/admin/masterclass" className="text-xs text-[hsl(var(--text-tertiary))] hover:text-[#c9a227] inline-flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3 h-3" /> Volver a Masterclasses
            </Link>
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#c9a227]" /> {data.masterclass.title}
            </h1>
            <p className="text-xs text-[hsl(var(--text-tertiary))] font-mono mt-1">/{slug}{phaseLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/masterclass/${slug}`} target="_blank" className="text-sm text-[#c9a227] hover:underline inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> Landing pública
            </Link>
            <Button onClick={clone} variant="ghost" className="text-[hsl(var(--text-secondary))]" data-testid="admin-mc-edit-clone">
              <Copy className="w-4 h-4 mr-2" /> Clonar
            </Button>
          </div>
        </div>

        {/* Status select & toggles */}
        <div className="card-soft mb-8 space-y-4">
          <h2 className="text-lg font-heading mb-2">Estado y visibilidad</h2>
          <Field label="Estado">
            <select value={form.status || 'draft'} onChange={e => update('status', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm bg-white" data-testid="admin-mc-status">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border))] bg-white cursor-pointer">
              <input type="checkbox" checked={!!form.show_homepage_banner} onChange={e => update('show_homepage_banner', e.target.checked)} className="w-4 h-4 accent-[#c9a227]" data-testid="admin-mc-banner" />
              <div className="text-sm"><p className="font-medium">Banner en la Home</p><p className="text-[11px] text-[hsl(var(--text-tertiary))]">Banda discreta en la portada general.</p></div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border))] bg-white cursor-pointer">
              <input type="checkbox" checked={form.show_nav_link !== false} onChange={e => update('show_nav_link', e.target.checked)} className="w-4 h-4 accent-[#c9a227]" />
              <div className="text-sm"><p className="font-medium">Enlace en navegación</p><p className="text-[11px] text-[hsl(var(--text-tertiary))]">(Reservado para uso futuro)</p></div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border))] bg-white cursor-pointer">
              <input type="checkbox" checked={form.show_formaciones_card !== false} onChange={e => update('show_formaciones_card', e.target.checked)} className="w-4 h-4 accent-[#c9a227]" />
              <div className="text-sm"><p className="font-medium">Card en /formaciones</p><p className="text-[11px] text-[hsl(var(--text-tertiary))]">(Reservado para uso futuro)</p></div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border))] bg-white cursor-pointer">
              <input type="checkbox" checked={form.show_limites_upsell_card !== false} onChange={e => update('show_limites_upsell_card', e.target.checked)} className="w-4 h-4 accent-[#c9a227]" />
              <div className="text-sm"><p className="font-medium">Upsell de Límites en /mi-camino</p><p className="text-[11px] text-[hsl(var(--text-tertiary))]">Solo a quien no tenga ya Límites.</p></div>
            </label>
          </div>
        </div>

        {/* Content */}
        <div className="card-soft mb-8 space-y-4">
          <h2 className="text-lg font-heading mb-2">Contenido</h2>
          <Field label="Título">
            <input value={form.title || ''} onChange={e => update('title', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" data-testid="admin-mc-title" />
          </Field>
          <Field label="Subtítulo">
            <input value={form.subtitle || ''} onChange={e => update('subtitle', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" />
          </Field>
          <Field label="Descripción">
            <textarea rows={6} value={form.description || ''} onChange={e => update('description', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" />
          </Field>
          <Field label="Imagen de portada (Cloudinary URL)">
            <input value={form.cover_image_url || ''} onChange={e => update('cover_image_url', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-mono" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Fecha y hora (hora de Madrid)" hint={form.date_iso ? previewMadrid(form.date_iso) : 'Déjalo vacío si aún no hay fecha confirmada.'}>
              <input
                type="datetime-local"
                value={isoUtcToMadridLocal(form.date_iso)}
                onChange={e => update('date_iso', madridLocalToIsoUtc(e.target.value))}
                className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                data-testid="admin-mc-date"
              />
            </Field>
            <Field label="Duración (min)">
              <input type="number" value={form.duration_minutes || 90} onChange={e => update('duration_minutes', parseInt(e.target.value, 10))} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" />
            </Field>
          </div>
          <Field label="Link Google Meet" hint="Visible para inscritas dentro de su panel.">
            <input value={form.meet_url || ''} onChange={e => update('meet_url', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-mono" placeholder="https://meet.google.com/xxx-xxxx-xxx" />
          </Field>
          <Field label="URL del CTA al curso completo">
            <input value={form.cta_target_url || ''} onChange={e => update('cta_target_url', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" />
          </Field>
          <Field label="Canal de WhatsApp (destino del botón de registro)" hint="Si lo rellenas, el botón «Quiero reservar mi plaza» de la landing pública lleva DIRECTAMENTE a este enlace (nueva pestaña). Si lo dejas vacío, el botón usa el flujo clásico de inscripción por email.">
            <input value={form.whatsapp_channel_url || ''} onChange={e => update('whatsapp_channel_url', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-mono" placeholder="https://chat.whatsapp.com/... o https://whatsapp.com/channel/..." data-testid="admin-mc-whatsapp" />
          </Field>
        </div>

        {/* Recording */}
        <div className="card-soft mb-8 space-y-4">
          <h2 className="text-lg font-heading mb-2">Grabación (48 h)</h2>
          <Field label="URL de la grabación" hint="Vimeo, YouTube no listado, o link directo.">
            <input value={form.recording_url || ''} onChange={e => update('recording_url', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-mono" />
          </Field>
          <Field label="Disponible hasta (UTC ISO)" hint="Si lo dejas en blanco, no se muestra. Tras esta fecha vuelve a ocultarse.">
            <input value={form.recording_available_until || ''} onChange={e => update('recording_available_until', e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-mono" />
          </Field>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[hsl(var(--border))]">
            <div className="text-xs text-[hsl(var(--text-tertiary))]">
              {data.masterclass.recording_available_now ? (
                <span className="inline-flex items-center gap-1 text-green-700">● Grabación visible ahora</span>
              ) : recordingUntil ? (
                <span>Grabación cerrada o caducada</span>
              ) : (
                <span>Sin grabación activa</span>
              )}
              {recordingUntil && <span className="ml-2">· hasta {new Date(recordingUntil).toLocaleString('es-ES')}</span>}
            </div>
            <Button onClick={activateRecording} disabled={activating} className="border border-[#c9a227] text-[#8a7428] bg-white hover:bg-[#fdfbf5]" data-testid="admin-mc-activate-recording">
              <RefreshCw className="w-4 h-4 mr-2" />
              {activating ? 'Activando…' : 'Activar 48 h desde ahora'}
            </Button>
          </div>
        </div>

        {/* Save / Delete */}
        <div className="flex justify-between mb-10 flex-wrap gap-3">
          <Button onClick={remove} variant="ghost" className="text-red-600 hover:bg-red-50" data-testid="admin-mc-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Eliminar masterclass
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#c9a227] hover:bg-[#b89220] text-white" data-testid="admin-mc-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>

        {/* Enrollments */}
        <div className="card-soft">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#c9a227]" /> Inscritas</h2>
              <p className="text-xs text-[hsl(var(--text-tertiary))]">{data.enrollments_count} {data.enrollments_count === 1 ? 'inscripción' : 'inscripciones'}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={exportCSV} disabled={!data.enrollments_count}>Exportar CSV</Button>
          </div>
          {data.enrollments_count === 0 ? (
            <p className="text-sm text-[hsl(var(--text-tertiary))] italic">Sin inscripciones todavía.</p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]">
              {data.enrollments.map(e => (
                <li key={e.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{e.name || '(sin nombre)'}</p>
                    <p className="text-[hsl(var(--text-tertiary))] text-xs">{e.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-[hsl(var(--text-tertiary))]">{new Date(e.enrolled_at).toLocaleString('es-ES')}</span>
                    <button onClick={() => removeEnrollment(e.id)} className="text-red-600 hover:text-red-800" title="Eliminar inscripción"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
